import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCServicesApi, RCCTooltip } from "react-common-components";
import { Link } from "react-router-dom";
import { useTranslation } from "src/context/Translations/context";
import { DivWithMarginBottom } from "../FiltersContainer/styles";
import Loader from "../Loader";
import Title from "../Title";
import { IFiltersTagsContainer, IRefFiltersTagsContainer, ITags } from "./interface";
import StyledFieldset, { FilterButtons } from "./styles";

const FiltersTagsContainer: React.ForwardRefRenderFunction<IRefFiltersTagsContainer, IFiltersTagsContainer> = ({
	onSubmitFilters,
	showFilters = false,
	tagType,
	defaultValue
}, ref) => {
	const { translate } = useTranslation();

	const [tagsLoaded, setTagsLoaded] = useState(false);

	const [loading, setLoading] = useState(true);
	const [tags, setTags] = useState<ITags[]>([]);
	const [selectedTags, setSelectedTags] = useState<number[]>(defaultValue ?? []);
	const [response, setResponse] = useState<string>("");
	
	const loadTags = async () => {
		setLoading(true);
		const {data} = await RCCServicesApi.getRequest({
			method: "Tags/ListAll",
			urlParameters: {
				use: tagType
			}
		});
		setTags(data as ITags[]);
		setLoading(false);
		setTagsLoaded(true);
	};

	useEffect(() => {
		if(!showFilters || tagsLoaded)
			return;

		loadTags();
	}, [showFilters, tagsLoaded]);

	const removeTag = (id: number) => {
		setSelectedTags((prevState) => prevState.filter((tag) => tag !== id));
	};

	const addTag = (id: number) => {
		setSelectedTags((prevState) => [...prevState, id]);
	};

	const resetFilters = () => {
		setSelectedTags([]);
	};

	useEffect(() => {
		setResponse(selectedTags.toString());
	}, [selectedTags]);

	useImperativeHandle(ref, () => ({
		response
	}));

	return(
		<RCCGrid
			container
			styleInline="z-index:200;"
		>
			<StyledFieldset showFilters={showFilters}>
				<legend>
					<Title size="xs" color="purple">
						Tags
					</Title>
				</legend>
				<>
					<DivWithMarginBottom>
						{loading ? (
							<Loader />
						): (
							<>
								{!tags || tags.length === 0 ? (
									<Link to={`/tags/?use=${tagType}`}>
										<RCCAlert 
											title={translate("Atenção")}	
											description={translate("Não foram encontrados resultados para esta pesquisa, aperte para ir a tela de cadastro")}
										/>
									</Link>
								): (
									<RCCGrid container item>
										{tags.map((tag) => {
											if(selectedTags.includes(tag.taa014Id))
												return <RCCButton 
													key={`tags-${tag.taa014Id}`}
													color="purple"
													icon={{
														name: "check"
													}}
													onClick={() => removeTag(tag.taa014Id)}
												>{tag.taa014Descricao}</RCCButton>;

											return <RCCButton 
												key={`tags-${tag.taa014Id}`}	
												color="grey"
												icon={{
													name: "close"
												}}
												onClick={() => addTag(tag.taa014Id)}
											>{tag.taa014Descricao}</RCCButton>;
										})}
									</RCCGrid>
								)}
							</>
						)}
					</DivWithMarginBottom>
					<FilterButtons>
						{tags && tags.length > 0 && (
							<Link to={`/tags/?use=${tagType}`}>
								<RCCTooltip
									text={translate("Ir a tela de cadastro")}
									color="grey"
								>
									<RCCButton
										icon={{name: "tags", color: "#FFF", size: 16}}
										color="grey"
										layout="isRounded"
										size="xs"
									/>
								</RCCTooltip>
							</Link>
						)}
						<RCCTooltip
							text={`${translate("Recarregar")} tags`}
							color="grey"
						>
							<RCCButton
								icon={{name: "refresh", color: "#FFF", size: 16}}
								color="grey"
								layout="isRounded"
								size="xs"
								onClick={loadTags}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Aplicar filtro")}
							color="blue"
						>
							<RCCButton
								icon={{name: "search", color: "#FFF", size: 16}}
								color="blue"
								layout="isRounded"
								size="xs"
								onClick={onSubmitFilters}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Limpar filtros")}
							color="yellow"
						>
							<RCCButton
								icon={{name: "broom", color: "#FFF", size: 16}}
								color="yellow"
								layout="isRounded"
								size="xs"
								onClick={resetFilters}
							/>
						</RCCTooltip>
					</FilterButtons>
				</>
			</StyledFieldset>
		</RCCGrid>
	);};

export default forwardRef(FiltersTagsContainer);
